import { Component, OnInit } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { lastValueFrom } from 'rxjs';
import { CompaniesService } from 'src/app/settings/services/companies.service';
import constants from 'src/app/shared/constants';

const VALID_LOGO_TYPES = ['image/jpeg', 'image/png'];
const MAX_SIZE_IN_BYTES = 300 * 1024; // 300 KB

@Component({
  selector: 'app-company-edit-customize',
  templateUrl: './company-edit-customize.component.html',
  styleUrls: ['./company-edit-customize.component.scss'],
})
export class CompanyEditCustomizeComponent implements OnInit {
  mensaje: SafeHtml;
  showColorSelector = false;

  currentCustomField: string;

  customColorSelected = {
    primary: false,
  };

  primaryColors = [
    {
      selected: false,
      color: '#eab910',
      selectedColor: '#000000',
    },
  ];

  constructor(
    private readonly sanitizer: DomSanitizer,
    private readonly translateService: TranslateService,
    public companiesService: CompaniesService,
    private readonly snackBar: MatSnackBar
  ) {}

  ngOnInit(): void {
    this.getTranslation();
    this.configureColorPalette();
  }

  private configureColorPalette(): void {
    if (this.companiesService.currentCompany.colorPalette?.primary) {
      this.setSelectedColors();
    } else {
      this.setDefaultColors();
    }
  }

  private setSelectedColors(): void {
    this.checkSelectedColor(this.primaryColors, 'primary');
  }

  private setDefaultColors(): void {
    this.companiesService.currentCompany.colorPalette = {
      primary: this.primaryColors[0].color,
    };
    this.primaryColors[0].selected = true;
  }

  checkSelectedColor = (array, key) => {
    if (this.companiesService.currentCompany.colorPalette[key]) {
      let find = false;
      for (let color of array) {
        if (color.color.toUpperCase() === this.companiesService.currentCompany.colorPalette[key].toUpperCase()) {
          color.selected = true;
          find = true;
        } else {
          color.selected = false;
        }
      }
      if (!find) this.customColorSelected[key] = true;
    }
  };

  getTranslation = async () => {
    this.mensaje = this.sanitizer.bypassSecurityTrustHtml(
      await lastValueFrom(this.translateService.get('Settings.UploadLogoDetails'))
    );
  };

  removeIcon() {
    this.companiesService.currentCompany.logo = undefined;
  }

  onSelectNewColor(field) {
    this.currentCustomField = field;
    this.showColorSelector = true;
  }

  setColor(color, field) {
    this.customColorSelected[field] = false;
    this.showColorSelector = false;
    this[field + 'Colors'].map((c) => (c.selected = false));
    color.selected = true;
    if (this.companiesService.currentCompany?.colorPalette)
      this.companiesService.currentCompany.colorPalette[field] = color.color;
  }

  onNewColor(color) {
    this.showColorSelector = false;
    if (color) {
      this.customColorSelected[this.currentCustomField] = true;

      this[this.currentCustomField + 'Colors'].map((c) => (c.selected = false));
      if (this.companiesService.currentCompany?.colorPalette)
        this.companiesService.currentCompany.colorPalette[this.currentCustomField] = color;
    }
  }

  showLogoError(type: string) {
    this.snackBar.open(
      this.translateService.instant(`Settings.${type}`),
      this.translateService.instant('Shared.Close'),

      { duration: constants.snackBarErrorDuration }
    );
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files?.[0]) {
      return this.showLogoError('UploadLogoDetails_error_2'); // No file selected
    }

    const file = input.files[0];

    if (!this.isValidFileType(file)) {
      return this.showLogoError('UploadLogoDetails_error_1'); // Invalid file type
    }

    if (!this.isValidFileSize(file)) {
      return this.showLogoError('UploadLogoDetails_error_1'); // File size exceeds limit
    }

    this.processFile(file);
  }

  private isValidFileType(file: File): boolean {
    return VALID_LOGO_TYPES.includes(file.type);
  }

  private isValidFileSize(file: File): boolean {
    return file.size <= MAX_SIZE_IN_BYTES;
  }

  private processFile(file: File): void {
    const reader = new FileReader();

    reader.onload = () => {
      const img = new Image();

      img.onload = () => {
        if (!this.isValidImageDimensions(img)) {
          return this.showLogoError('UploadLogoDetails_error_1'); // Invalid dimensions
        }

        // If all validations pass, set the logo
        this.companiesService.currentCompany.logo = reader.result as string;
        this.companiesService.companyLogoErrorState = false;
      };

      img.onerror = () => {
        this.showLogoError('UploadLogoDetails_error_2'); // Invalid image file
      };

      img.src = reader.result as string;
    };

    reader.readAsDataURL(file);
  }

  private isValidImageDimensions(img: HTMLImageElement): boolean {
    return img.width <= 300 && img.height <= 300;
  }
}
